export const ONBOARDING_CHAIN_STEPS = [
  'brand-facts',
  'competitors',
  'customer-profile',
  'strategy',
  'visual-style',
] as const

export type OnboardingChainStep = (typeof ONBOARDING_CHAIN_STEPS)[number]

/** The steps that have a screen today; setup is finished once every one is done. */
export const BUILT_ONBOARDING_CHAIN: readonly OnboardingChainStep[] = [
  'brand-facts',
  'competitors',
  'customer-profile',
  'strategy',
  'visual-style',
]

export type OnboardingChainStepState = 'done' | 'now' | 'todo'

export interface OnboardingChainStepView {
  readonly step: OnboardingChainStep
  readonly index: number
  readonly state: OnboardingChainStepState
  readonly built: boolean
  readonly fresh: boolean
}

const STEP_MESSAGE_SEGMENT: Record<OnboardingChainStep, string> = {
  'brand-facts': 'brandFacts',
  competitors: 'competitors',
  'customer-profile': 'customerProfile',
  strategy: 'strategy',
  'visual-style': 'visualStyle',
}

export type StepLockedPart = 'title' | 'body' | 'alertTitle' | 'alertBody' | 'alertConfirm'

export function stepLockedKey(step: OnboardingChainStep, part: StepLockedPart): string {
  return `setup.locked.${STEP_MESSAGE_SEGMENT[step]}.${part}`
}

export function stepTitleKey(step: OnboardingChainStep): string {
  return `setup.steps.${STEP_MESSAGE_SEGMENT[step]}.title`
}

export function stepDescriptionKey(step: OnboardingChainStep): string {
  return `setup.steps.${STEP_MESSAGE_SEGMENT[step]}.description`
}

export function isOnboardingChainStep(value: unknown): value is OnboardingChainStep {
  return typeof value === 'string' && (ONBOARDING_CHAIN_STEPS as readonly string[]).includes(value)
}

export function isBuiltOnboardingChainStep(step: OnboardingChainStep): boolean {
  return BUILT_ONBOARDING_CHAIN.includes(step)
}

/** The shipped step before this one, which Back on this step's first screen leads to; `null` on the first shipped step. */
export function previousBuiltStep(
  step: OnboardingChainStep,
  built: readonly OnboardingChainStep[] = BUILT_ONBOARDING_CHAIN,
): OnboardingChainStep | null {
  const index = built.indexOf(step)
  return index > 0 ? (built[index - 1] ?? null) : null
}

export interface StepConfirmation {
  readonly confirmed: boolean
  readonly confirmedAt: string | null
}

export type OnboardingChainConfirmations = Partial<Record<OnboardingChainStep, StepConfirmation>>

export type OnboardingChainDone = Readonly<Record<OnboardingChainStep, boolean>>

/** Which steps are done: a shipped step is done when its check passes, it has a stamp, the step before it is done and its stamp is not older than that step's; mirrors the server's `chainPresence`. */
export function chainDone(
  confirmations: OnboardingChainConfirmations,
  built: readonly OnboardingChainStep[] = BUILT_ONBOARDING_CHAIN,
): OnboardingChainDone {
  const done = Object.fromEntries(ONBOARDING_CHAIN_STEPS.map((step) => [step, false])) as Record<
    OnboardingChainStep,
    boolean
  >
  let previousAt: string | null = null
  for (const step of built) {
    const confirmation = confirmations[step]
    if (!confirmation?.confirmed || confirmation.confirmedAt === null) break
    if (previousAt !== null && confirmation.confirmedAt < previousAt) break
    done[step] = true
    previousAt = confirmation.confirmedAt
  }
  return done
}

/** The shipped steps after a step, in chain order. */
export function laterBuiltSteps(
  step: OnboardingChainStep,
  built: readonly OnboardingChainStep[] = BUILT_ONBOARDING_CHAIN,
): OnboardingChainStep[] {
  const position = ONBOARDING_CHAIN_STEPS.indexOf(step)
  return built.filter((later) => ONBOARDING_CHAIN_STEPS.indexOf(later) > position)
}

/** True when a stamp is not older than the upstream stamp it depends on; `null` means never. */
export function isCurrentSince(stamp: string | null, upstream: string | null): boolean {
  if (upstream === null) return true
  if (stamp === null) return false
  return stamp >= upstream
}
